import { useMemo } from 'react';

import { ExpandedNavigationItem, createMegaMenuBlocks } from '~/blocks/Header/const';
import { DEFAULT_DISCOVER_URL } from '~/const';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useContentAvailability from '~/hooks/useContentAvailability';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { USER_ROLES } from '~/pages/const';
import {
  ANALYTICS_DASHBOARD_ROUTE,
  ANALYTICS_ROUTE,
  DISCOVER_SEARCH_ROUTE,
  DiscoverRoutes,
  HOME_ROUTE,
  LEARNER_AL_ROUTE,
  MANAGER_AL_ROUTES,
} from '~/routes';
import { NavigationItem, Video, VideoCategory } from '~/types';
import { replaceByIndex } from '~/utils/arrayUtils/replaceByIndex';
import { getCourseUrl } from '~/utils/urlUtils/getCourseUrl';

export type CourseDropdownItem = {
  id?: string;
  name?: string | null;
  navigationDescription?: string | null;
  slug?: string;
  isStarted: boolean;
};

const useMenuItems = ({
  navigationItems,
  dropdownCourses,
  assignedLearningsName,
  sortedCategories,
  twoLatestVideos,
}: {
  navigationItems: NavigationItem[];
  dropdownCourses: CourseDropdownItem[] | undefined;
  assignedLearningsName?: string;
  sortedCategories: VideoCategory[];
  twoLatestVideos: Video[];
}) => {
  const { userInfo } = useUserInfoContext();
  const { t } = useTenantTranslation();
  const contentAvailability = useContentAvailability();
  const isSsku = useIsSsku();

  const menuItems = useMemo(() => {
    if (userInfo) {
      const discoverMegaMenuBlocks = createMegaMenuBlocks({
        isSsku,
        t,
        contentAvailability,
        baseCategoriesList: sortedCategories,
        twoLatestVideos,
      });
      const shouldRenderDiscoverAsMegaMenu = discoverMegaMenuBlocks.length > 1;

      const items: ExpandedNavigationItem[] = [
        {
          title: t(`header.home`),
          url: HOME_ROUTE,
          className: 'home-link',
          type: 'link',
        },
        {
          url: shouldRenderDiscoverAsMegaMenu ? DEFAULT_DISCOVER_URL : DISCOVER_SEARCH_ROUTE,
          title: t(`header.explore`),
          blocks: discoverMegaMenuBlocks,
          type: shouldRenderDiscoverAsMegaMenu ? 'MegaMenu' : 'link',
        },
        {
          title: t(`header.analytics`),
          url: ANALYTICS_ROUTE,
          managerUrl: ANALYTICS_DASHBOARD_ROUTE,
          avoidManagerUrlWithoutSub: true,
          type: 'link',
        },
      ];

      if (isSsku) {
        return items;
      }

      // add asl url if needed
      if (
        (userInfo?.teams && userInfo.teams.length > 0) ||
        userInfo?.role === USER_ROLES.CompanyOwner ||
        userInfo?.role === USER_ROLES.CompanyManager
      ) {
        items.push({
          url: userInfo?.is_manager ? MANAGER_AL_ROUTES.List : LEARNER_AL_ROUTE,
          title: `${assignedLearningsName || t('header.assigned')} ${t('header.learning')}`,
          type: 'link',
        });
      }

      return items;
    } else {
      const links = navigationItems?.filter(
        (item) => item.type === 'Link' || item.type === 'MegaMenu',
      );
      let loggedOutLinksData: NavigationItem[] = [];

      const indexOfCourseLink = links.findIndex((item) => item?.url === DiscoverRoutes.Courses);

      //replace url to single course if only one course
      if (indexOfCourseLink !== -1 && dropdownCourses?.length === 1 && dropdownCourses[0]) {
        loggedOutLinksData = replaceByIndex(links, indexOfCourseLink, {
          ...loggedOutLinksData[indexOfCourseLink],
          url: getCourseUrl(dropdownCourses[0]?.slug) || '',
        });
      } else {
        loggedOutLinksData = links;
      }

      return loggedOutLinksData;
    }
  }, [
    navigationItems,
    userInfo,
    dropdownCourses,
    contentAvailability,
    t,
    sortedCategories,
    assignedLearningsName,
    twoLatestVideos,
    isSsku,
  ]);

  return menuItems;
};

export default useMenuItems;
